/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Spinner } from 'reactstrap';
import * as default_data from '../config.js'
import { setWithExpiry } from '../AuthService.js';
import WarningPopup from '../Popups/WarningPopup.js';
import Modal from 'react-modal';
import { Alert, Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import { MdMail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { getBannerColor } from '../Utils/classification_helper.js';
import { getLoginLogo } from '../Utils/file_functions.js';

function LoginForm() {

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [triggerWarningPopup, setTriggerWarningPopup] = useState(0);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    fetch(default_data.default_user_service_url_no_cac + '/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        password: password
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while logging in.');
      })
      .then(data => {
        if(data.status != 200) {
            if(data.response === 'You need to verify your email') {
               window.location = '/verify-email';
               return;
            }
            else {
               throw new Error(data.response);
            }
        }
        data = data.response;

        setIsLoading(false);
        setErrorMessage('');
        
        // Save the access token and user data to local storage and redirect to the dashboard
        setWithExpiry('access_token', data.access_token);
        setWithExpiry('user', JSON.stringify(data.user));

        if(data.user.mfa_required && data.user.mfa_required == true) {
          window.location = '/mfa-login';
        }
        else if(data.user.new_password_required && data.user.new_password_required == true) {
          window.location = '/update?action=force_password';
        }
        else {
          window.location = '/dashboard';
        }
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        if(error.message.toString().toLowerCase().indexOf('failed to fetch') > -1) {
          setErrorMessage('Failed to connect due to our firewall blocking your IP. Please reach out to ' + default_data.default_support_email + ' with your IP so we can whitelist it (https://whatismyipaddress.com/).');
        }
        else {
          setErrorMessage(error.message);
        }        
      });
  };

  function loginCAC() {
    setIsLoading(true);

    fetch(default_data.default_user_service_url + '/login-cac', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while logging in.');
      })
      .then(data => {
        if(data.status != 200) {
            if(data.status === 404) {
              setIsLoading(false);
              window.location = '/register';
              return;
            }
            throw new Error(data.response);
        }
        data = data.response;

        setIsLoading(false);
        setErrorMessage('');
        
        // Save the access token and user data to local storage and redirect to the dashboard
        setWithExpiry('access_token', data.access_token);
        setWithExpiry('user', JSON.stringify(data.user));

        window.location = '/dashboard';
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        if(error.message.toString().toLowerCase().indexOf('failed to fetch') > -1) {
          setErrorMessage('Failed to connect for two possibles reasons: 1. Your browser has blocked the CAC/PIV passthrough. Please close all your tabs, close your browser and try again. 2. If this did not work, our firewall (or yours) might blocking the traffic. Please reach out to ' + default_data.default_support_email + ' with your IP so we can whitelist it (Google "what is my IP").');
        }
        else {
          setErrorMessage(error.message);
        }        

      });
  }

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  Modal.setAppElement('#root');

  return (
    <div>
      { 
        <div className={`header-message ${getBannerColor(false)}`}>
          { default_data.default_banner_override != null && default_data.default_banner_override != '' ?
              default_data.default_banner_override : 'UNCLASSIFIED' 
          }
        </div>            
      }
      <div style={{  backgroundColor: '#121212', height: 'calc(100vh - 16px)'}}>
        <Stack sx={{maxHeight: 'calc(100vh - 16px)'}} direction={"row"} spacing={0}>
          <Box sx={{width: isTablet ? "100vw" : "50vw" , overflowY: 'auto'}} >
            <Container>
              <Box style={{display: 'flex', justifyContent: isTablet ? 'center' : 'start', marginBottom: '8px', marginTop: '32px'}}>
                <a href="/">
                  <img src="/logo.png" style={{width: isTablet ? '90px' : '120px'}} alt="Ask Sage" className="logo" />
                </a>
              </Box>
              {/* Form Body */}
              <Row className="justify-content-center ">
                <Col xs="12" md="8" lg="8">
                  <h2 style={{ color: 'white', fontWeight: '500' }}>
                    {default_data.default_cobranding_name ? default_data.default_cobranding_name : 'Welcome!'}
                  </h2>
                  { default_data.default_hide_login === false ? 
                  <Form onSubmit={handleSubmit} className='login-form'>
                    <FormGroup>
                        <Label for="email" >Email</Label>
                        <div className='input-wrapper'>
                          <Input 
                            type="email" 
                            name="email" 
                            id="email" 
                            value={email} 
                            onChange={event => setEmail(event.target.value)} 
                          />
                          <MdMail className="input-icon" />
                        </div>
                    </FormGroup>
                    <FormGroup noMargin>
                      <Label for="password">Password</Label>
                      <div className='input-wrapper'>
                        <Input
                          type={isPasswordVisible ? 'text' : 'password'}
                          name="password"
                          id="password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                        />
                        {isPasswordVisible ? (
                          <MdVisibilityOff className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        ) : (
                          <MdVisibility className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        )}
                      </div>
                    </FormGroup>
                    { default_data.default_hide_login === false && (
                      <Box style={{ width: '100%', display: 'flex', justifyContent: 'end', marginBottom: '8px'}}>
                        <Button style={{color: 'white', fontSize: '14px'}} color={'link'} onClick={() => window.location.href = '/lost-pass'}>
                          Forgot password?
                        </Button>
                      </Box>
                    )}
                    {errorMessage && <Alert severity='error' sx={{mb: 2}}>{errorMessage}</Alert>}
                    <Button type="submit" disabled={isLoading} className='login-form-button'>
                        {isLoading ? <Spinner size="sm" /> : 'Sign In'}
                    </Button>
                  </Form> : ''}
                  { default_data.default_hide_cac === true ? '' :   
                    <Button style={{margin: '20px 0'}} block onClick={() => loginCAC()} className='login-form-button'>
                    Sign in with a CAC/PIV (Sign Up First)
                    </Button>
                  }
                  {
                    default_data.default_saml_enabled === true ? 
                      <Button className={'login-form-button'} style={{margin: '20px 0'}} block onClick={() => window.location.href = '/saml-redirect'}>
                      Sign in with your Enterprise SSO
                      </Button>
                    : ''  
                  }
                  <Button className='login-form-button-blue' style={{margin: '20px 0'}} block onClick={() => window.location.href = '/register'}>
                    Sign Up
                  </Button>
                </Col>
              </Row>
              {/* Footer */}
              <Row className="justify-content-center reg-page-mode-footer" style={{fontSize: '14px'}}>
                <Col xs="12" md="12" lg="12">
                  <a className='white' href="/terms" target="_blank">Terms of Service</a> | <a className='white' href="/privacy" target="_blank">Privacy Policy</a>
                  <br /><br />
                  Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
                </Col>
              </Row>
            </Container>  
          </Box>
          {!isTablet && (
            <div
              className="space-image"
              style={{
                backgroundImage: "url('/space-background.png')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                width: '50vw',
              }}
            >
              <img src={getLoginLogo()} alt="Space Girl" />
            </div>
          )}
        </Stack>
        <WarningPopup setTriggerWarningPopup={setTriggerWarningPopup} triggerWarningPopup={triggerWarningPopup} />
      </div>    
    </div>    
  );
}

export default LoginForm;
